/**
 * 10.9HN — DIVISAS NATURAIS entre territórios (Thiago: "divisões por água, lava… com pontes").
 * O muro de pedra entre os territórios vira um RIO (água), um rio de LAVA (bioma Brasa), uma FENDA
 * DE GELO (bioma Gelo) ou um ABISMO do Vazio (Terras Fraturadas); a porta vira PONTE. A colisão
 * geométrica das linhas da grade continua igual (TerritoryGates.block) — só o visual muda, e a
 * faixa bloqueada ganhou a largura do rio (DIVIDE_HALF).
 *
 * 10.9HO — MOVIMENTO (Thiago: "deixar melhor, com movimento"): o shader recebe do vértice a
 * coordenada AO LONGO do rio (projeção no eixo local da faixa) e a posição da linha, então a
 * correnteza corre no sentido do rio (direção fixa por linha, sorteada), com riscos alongados,
 * espuma andando, brilhos; lava com crosta escorrendo, bolhas e pulsos; gelo com névoa e
 * cintilação; abismo com veios escorrendo pro centro e motes.
 *
 * Cada tipo é UM ShaderMaterial (animado por uTime) num InstancedMesh por região: 4 draw calls no
 * máximo por região pra todas as divisas. Névoa incluída (senão o rio brilharia ao longe).
 */
import * as THREE from "three";

export type DivideType = "water" | "lava" | "ice" | "chasm";
export const DIVIDE_TYPES: DivideType[] = ["water", "lava", "ice", "chasm"];
/** meia-largura da faixa (visual E colisão) */
export const DIVIDE_HALF = 2.6;
/** cores no mapa/minimapa */
export const DIVIDE_MAP_COLOR: Record<DivideType, string> = { water: "#3d8bff", lava: "#ff7a2a", ice: "#bfe8ff", chasm: "#9d5cff" };
/** cor das pedras/cacos da margem por tipo */
export const DIVIDE_BANK_COLOR: Record<DivideType, number> = { water: 0x5a6478, lava: 0x1c1216, ice: 0xd8ecf8, chasm: 0x2a1c44 };
/** partículas ambientes por tipo (cor, velocidade, duração) */
export const DIVIDE_AMBIENT: Record<DivideType, { color: number; speed: number; dur: number; chance: number; count: number }> = {
  water: { color: 0xe8f8ff, speed: 2.2, dur: 0.45, chance: 0.35, count: 2 },
  lava: { color: 0xff8a2a, speed: 1.3, dur: 1.5, chance: 0.9, count: 2 },
  ice: { color: 0xffffff, speed: 0.6, dur: 0.9, chance: 0.5, count: 1 },
  chasm: { color: 0xb07cff, speed: 1.1, dur: 1.7, chance: 0.8, count: 2 },
};

/** Tipo da divisa entre dois biomas (a/b podem ser null na borda). */
export function divideTypeFor(a: string | null, b: string | null, regionId: string): DivideType {
  const bi = [a, b];
  if (bi.includes("ember")) return "lava";
  if (bi.includes("frost")) return "ice";
  if (regionId === "shatteredWilds") return bi.some((x) => x === "shard" || x === "crystal") ? "water" : "chasm";
  if (regionId === "tempestReach") return bi.some((x) => x === "ruin" || x === "thunder") ? "chasm" : "water";
  return "water";
}

const PALETTE: Record<DivideType, { a: number; b: number }> = {
  water: { a: 0x0b3466, b: 0x3fa0d8 },
  lava: { a: 0x2a0e08, b: 0xff6a1a },
  ice: { a: 0x8fc4e8, b: 0xe8f8ff },
  chasm: { a: 0x2a0f4a, b: 0x9d5cff },
};

const VERT = /* glsl */ `
  #include <fog_pars_vertex>
  varying vec2 vUv;
  varying float vAlong;  // metros ao longo do rio (contínuo entre segmentos da mesma linha)
  varying float vLine;   // posição da linha (constante por linha: sorteia direção/semente)
  void main() {
    vUv = uv;
    vec4 wp = vec4( position, 1.0 );
    mat4 im = mat4( 1.0 );
    #ifdef USE_INSTANCING
      im = instanceMatrix;
    #endif
    wp = modelMatrix * im * wp;
    vec3 ax = normalize( ( modelMatrix * im * vec4( 1.0, 0.0, 0.0, 0.0 ) ).xyz ); // eixo do rio
    vec3 ay = normalize( ( modelMatrix * im * vec4( 0.0, 1.0, 0.0, 0.0 ) ).xyz ); // atravessa o rio
    vec3 c = ( modelMatrix * im * vec4( 0.0, 0.0, 0.0, 1.0 ) ).xyz;              // centro do segmento
    vAlong = dot( wp.xyz, ax );
    vLine = dot( c, ay );
    vec4 mvPosition = viewMatrix * wp;
    gl_Position = projectionMatrix * mvPosition;
    #include <fog_vertex>
  }`;

const FRAG = /* glsl */ `
  #include <fog_pars_fragment>
  uniform float uTime;
  uniform vec3 uA, uB;
  uniform int uType;
  varying vec2 vUv;
  varying float vAlong;
  varying float vLine;
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y);
  }
  void main() {
    float edge = smoothstep(0.0, 0.16, vUv.y) * smoothstep(1.0, 0.84, vUv.y); // margens somem no chão
    float center = 1.0 - abs(vUv.y - 0.5) * 2.0;                                // 1 no meio, 0 na margem
    float seed = hash(vec2(floor(vLine * 0.37), 7.0));
    float dir = seed > 0.5 ? 1.0 : -1.0;                                          // sentido da correnteza (por linha)
    float t = uTime;
    vec3 col; float alpha;
    if (uType == 0) {
      // ÁGUA — correnteza no sentido do rio: riscos alongados + duas camadas de ondulação + espuma andando
      float a = vAlong * dir + t * 1.6 + seed * 40.0;
      float w1 = vnoise(vec2(a * 0.35, vUv.y * 3.0 + seed * 9.0));
      float w2 = vnoise(vec2(a * 0.9 + t * 0.4, vUv.y * 5.0 - t * 0.15));
      float streak = smoothstep(0.55, 0.92, vnoise(vec2(a * 0.45, vUv.y * 11.0 + seed * 3.0)));   // riscos longos
      vec3 deep = mix(uB, uA, 0.35 + center * 0.55);                                              // mais fundo no meio
      col = deep * (0.75 + w1 * 0.35 + w2 * 0.25) + vec3(0.35, 0.55, 0.7) * streak * 0.35;
      float foamBank = (1.0 - smoothstep(0.0, 0.32, center)) * (0.5 + 0.5 * vnoise(vec2(a * 1.4, vUv.y * 9.0)));
      float foamPatch = smoothstep(0.78, 0.96, vnoise(vec2(a * 0.7, vUv.y * 6.0 + t * 0.2)));
      col += vec3(0.9, 0.97, 1.0) * (foamBank * 0.55 + foamPatch * 0.7);
      col += vec3(1.0) * pow(max(0.0, w2), 9.0) * 0.9;                                          // brilhos do sol
      alpha = 0.94 * edge + foamPatch * 0.25;
    } else if (uType == 1) {
      // LAVA — crosta escorrendo devagar, veios incandescentes, bolhas estourando, pulso de calor e margem em brasa
      float a = vAlong * dir * 0.5 + t * 0.22 + seed * 30.0;
      float crust = vnoise(vec2(a * 0.8, vUv.y * 4.0 + seed * 5.0));
      float veins = smoothstep(0.52, 0.74, vnoise(vec2(a * 2.2, vUv.y * 8.0 + t * 0.05)));
      float pulse = 0.8 + 0.2 * sin(t * 1.8 + vAlong * 0.35 + seed * 6.0);
      float bub = pow(vnoise(vec2(vAlong * 3.0 + seed * 11.0, vUv.y * 12.0) + vec2(0.0, t * 1.1)), 16.0) * 1.8;
      col = mix(uA, uB, veins * pulse) * (0.75 + crust * 0.45);
      col += vec3(1.0, 0.55, 0.15) * (veins * pulse * 0.9 + 0.06);                                // acima do threshold: brilha (sem estourar)
      col += vec3(1.0, 0.85, 0.5) * bub;
      col += vec3(1.0, 0.35, 0.08) * (1.0 - smoothstep(0.0, 0.35, center)) * 0.7;                   // margem incandescente
      alpha = 0.98 * edge;
    } else if (uType == 2) {
      // GELO — placa clara com fendas, névoa gelada deslizando e cintilação
      float n = vnoise(vec2(vAlong * 0.6, vUv.y * 3.0));
      float cr = 1.0 - smoothstep(0.0, 0.06, abs(vnoise(vec2(vAlong * 1.2 + 3.0, vUv.y * 4.5)) - 0.5));
      float mist = vnoise(vec2(vAlong * 0.25 + t * 0.35 * dir, vUv.y * 2.0 + t * 0.1)) * 0.35;
      float sparkle = pow(vnoise(vec2(vAlong * 6.0, vUv.y * 24.0) + t * 0.9), 14.0) * 1.2;
      col = mix(uA, uB, n) + cr * 0.35 + mist + sparkle;
      alpha = 0.93 * edge;
    } else {
      // ABISMO — quase preto; veios roxos escorrendo pro centro, pulso e motes
      float toward = (vUv.y - 0.5) * 6.0 + t * 0.9 * sign(vUv.y - 0.5);                          // escorre pra dentro
      float v = smoothstep(0.6, 0.85, vnoise(vec2(vAlong * 1.3 + seed * 9.0, toward)));
      float pulse = 0.7 + 0.3 * sin(t * 1.4 + vAlong * 0.25);
      float motes = pow(vnoise(vec2(vAlong * 5.0, vUv.y * 20.0) + t * 0.6), 18.0) * 2.0;
      col = mix(vec3(0.012, 0.008, 0.035), uA, v * 0.9) + uB * (v * 1.2 * pulse + motes);
      col *= 0.55 + 0.45 * (1.0 - center * 0.7);                                                   // fundo mais escuro no meio
      alpha = 0.97 * edge;
    }
    gl_FragColor = vec4(col, alpha);
    #include <fog_fragment>
  }`;

let _mats: Record<DivideType, THREE.ShaderMaterial> | null = null;

/** Materiais compartilhados (um por tipo). */
export function divideMaterials(): Record<DivideType, THREE.ShaderMaterial> {
  if (_mats) return _mats;
  const mk = (t: DivideType, idx: number): THREE.ShaderMaterial => new THREE.ShaderMaterial({
    uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.fog, {
      uTime: { value: 0 },
      uA: { value: new THREE.Color(PALETTE[t].a) },
      uB: { value: new THREE.Color(PALETTE[t].b) },
      uType: { value: idx },
    }]),
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    fog: true,
    side: THREE.DoubleSide,
  });
  _mats = { water: mk("water", 0), lava: mk("lava", 1), ice: mk("ice", 2), chasm: mk("chasm", 3) };
  return _mats;
}

export function tickDivides(t: number): void {
  if (!_mats) return;
  for (const k of DIVIDE_TYPES) _mats[k].uniforms.uTime.value = t;
}
